import React, { useEffect } from 'react'
import styles from './styles.module.css'
import Text from '../../../Text'
import { $textPrimary, $textTertiary } from '../../../../styles/colors/semantics'
import { useRegionSubscriptions } from '../../../RegionSurfaces'
import { price } from '../../../../utils/formats'
import AttributionLink from '../../../AttributionLink'

function GetFasterPricingTable({ plans = [], categories = [] }) {
  // Overmind→server-props (Phase A2.2): subscriptions come from the SSR-threaded
  // <RegionSurfacesProvider> (byte-identical to the store, which stays hydrated),
  // with an Overmind fallback for any non-page entry point.
  const { subscriptions, ensureRequested } = useRegionSubscriptions()

  useEffect(() => {
    ensureRequested()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getSubscriptionByCode = (code) => {
    return subscriptions.find(s => s.code === code)
  }

  const formatPriceDisplay = (subscription) => {
    if (!subscription || !subscription.price) return '€0'

    const priceData = subscription.price.monthly
    if (!priceData || !priceData.priceCents) return '€0'

    return price(priceData.priceCents, priceData.currency)
  }

  const getPriceForPlan = (plan, index) => {
    const subscriptionCodes = ['free', 'dedicated', 'competitive']
    const subscription = getSubscriptionByCode(subscriptionCodes[index]) || subscriptions[index]
    return formatPriceDisplay(subscription)
  }

  const renderValue = (value) => {
    if (value === 'check' || value === true) {
      return <span className={styles.checkMark} />
    }
    if (value === 'x' || value === false) {
      return <span className={styles.crossMark} />
    }
    return <Text type="small" color={$textPrimary.dark}>{value}</Text>
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.table}>
            {/* Header with plan info */}
            <div className={styles.tableHeader}>
              <div className={styles.headerCell} />
              {plans.map((plan, index) => {
                const priceDisplay = getPriceForPlan(plan, index)

                return (
                  <div className={styles.headerCell} key={index}>
                    <div className={styles.planName}>
                      <Text type="heading-6" color={$textPrimary.dark}>{plan.name}</Text>
                    </div>
                    <div className={styles.planPrice}>
                      <span className={styles.priceAmount}>{priceDisplay}</span>
                      <span className={styles.pricePeriod}>/month</span>
                    </div>
                    <div className={styles.planDescription}>{plan.description}</div>
                    <AttributionLink
                      className={styles.headerCta}
                      href="https://vrs.racing/signup?return_to=https%3A%2F%2Fapp.vrs.racing%2F%23%2FAccount%2FBilling"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {plan.ctaText || 'Subscribe now'}
                    </AttributionLink>
                  </div>
                )
              })}
            </div>

            {/* Feature categories and rows */}
            {categories.map((category, catIndex) => (
              <React.Fragment key={catIndex}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryName}>
                    <Text type="heading-8" color={$textPrimary.dark}>{category.name}</Text>
                  </div>
                </div>

                {category.features?.map((feature, featureIndex) => (
                  <div className={styles.tableRow} key={featureIndex}>
                    <div className={styles.rowLabel}>
                      <Text type="small" color={$textPrimary.dark}>{feature.name}</Text>
                    </div>
                    {feature.values?.map((value, planIndex) => (
                      <div className={styles.rowValue} key={planIndex}>
                        {renderValue(value)}
                      </div>
                    ))}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GetFasterPricingTable
